import type { PlaylistItem, Video } from '@crowdset/contracts';
import { parseYouTubeInput } from '@crowdset/domain';
import {
  ArrowDown,
  ArrowUp,
  LinkSimple,
  MagnifyingGlass,
  MusicNote,
  Plus,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { ApiError, errorMessage } from '../../lib/api';
import { searchMedia, usePlaylistMutations, useSession } from '../../lib/queries';
import { toast } from '../Toasts';
import { Badge, Button, cx, formatDuration, Input } from '../../ui/kit';

const REASON_LABEL: Record<string, string> = {
  not_found: 'Removed',
  private: 'Private',
  not_embeddable: 'Embedding off',
  live_or_upcoming: 'Live',
  unknown_duration: 'Unknown length',
  too_long: 'Too long',
  cooldown: 'Recently played',
  metadata_unavailable: 'Unchecked',
  region_blocked_everywhere: 'Blocked',
};

export function Thumb({
  video,
  className,
}: {
  video: Pick<Video, 'thumbnailUrl' | 'title'>;
  className?: string;
}) {
  return video.thumbnailUrl ? (
    <img
      src={video.thumbnailUrl}
      alt=""
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      className={cx('aspect-video shrink-0 rounded-md object-cover', className)}
    />
  ) : (
    <div
      className={cx(
        'flex aspect-video shrink-0 items-center justify-center rounded-md bg-ink-700 text-fg-subtle',
        className,
      )}
      aria-hidden
    >
      <MusicNote size={14} />
    </div>
  );
}

export function TrackRow({
  item,
  index,
  total,
  playlistId,
  compact,
  isNext,
}: {
  item: PlaylistItem;
  index: number;
  total: number;
  playlistId: string;
  compact?: boolean;
  isNext?: boolean;
}) {
  const m = usePlaylistMutations();
  const v = item.video;
  const move = (to: number) =>
    m.moveItem.mutate(
      { id: playlistId, itemId: item.id, toIndex: to },
      { onError: (e) => toast(errorMessage(e), 'error') },
    );
  return (
    <li
      className={cx(
        'group flex items-center gap-3 rounded-xl px-2 py-1.5',
        isNext ? 'bg-accent/[0.07] ring-1 ring-inset ring-accent/25' : 'hover:bg-white/[0.03]',
      )}
    >
      <span className="w-5 shrink-0 text-right font-mono text-xs text-fg-subtle">{index + 1}</span>
      <Thumb video={v} className={compact ? 'w-14' : 'w-20'} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm" title={v.title}>
          {v.title}
        </p>
        <p className="flex items-center gap-2 truncate text-xs text-fg-muted">
          <span className="truncate">{v.channelTitle}</span>
          <span className="font-mono text-fg-subtle">{formatDuration(v.durationMs)}</span>
          {isNext && <Badge tone="accent">Next up</Badge>}
          {v.ineligibleReason && (
            <Badge tone="warn">
              <WarningCircle size={11} weight="bold" aria-hidden />{' '}
              {REASON_LABEL[v.ineligibleReason] ?? 'Unplayable'}
            </Badge>
          )}
          {!v.ineligibleReason && v.durationMs !== null && v.durationMs > 10 * 60_000 && (
            <Badge tone="neutral">
              <span title="Rooms allow 10-minute tracks by default; owners can raise the limit.">
                Over 10 min · skipped in most rooms
              </span>
            </Badge>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center opacity-100 md:opacity-60 md:group-hover:opacity-100 md:focus-within:opacity-100">
        <button
          className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted hover:bg-white/[0.07] hover:text-fg disabled:opacity-30"
          disabled={index === 0}
          onClick={() => move(index - 1)}
          aria-label={`Move ${v.title} up`}
        >
          <ArrowUp size={14} weight="bold" />
        </button>
        <button
          className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted hover:bg-white/[0.07] hover:text-fg disabled:opacity-30"
          disabled={index === total - 1}
          onClick={() => move(index + 1)}
          aria-label={`Move ${v.title} down`}
        >
          <ArrowDown size={14} weight="bold" />
        </button>
        <button
          className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted hover:bg-danger/15 hover:text-[#f3a3a4]"
          onClick={() =>
            m.removeItem.mutate(
              { id: playlistId, itemId: item.id },
              { onError: (e) => toast(errorMessage(e), 'error') },
            )
          }
          aria-label={`Remove ${v.title}`}
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>
    </li>
  );
}

/** Paste a YouTube URL/ID (primary), or keyword search when enabled. Links are parsed, never fetched. */
export function AddTrackForm({ playlistId, compact }: { playlistId: string; compact?: boolean }) {
  const session = useSession().data!;
  const m = usePlaylistMutations();
  const [input, setInput] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'link' | 'search'>('link');
  const [toTop, setToTop] = useState(false);
  const searchEnabled = session.features.keywordSearch;
  const demo = session.mediaMode === 'fake';

  const add = (value: string, position: 'top' | 'bottom' = 'bottom') =>
    m.addItem.mutate(
      { id: playlistId, input: value, position },
      {
        onSuccess: (it) => {
          setInput('');
          setErr(null);
          toast(`Added “${it.video.title}”.`, 'ok');
        },
        onError: (e) => setErr(errorMessage(e)),
      },
    );

  return (
    <div className="flex flex-col gap-2">
      {searchEnabled && (
        <div role="tablist" aria-label="Add tracks by" className="flex gap-1 text-xs">
          {(['link', 'search'] as const).map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={mode === k}
              onClick={() => setMode(k)}
              className={cx(
                'rounded-lg px-2.5 py-1.5',
                mode === k ? 'bg-white/[0.08] text-fg' : 'text-fg-muted hover:text-fg',
              )}
            >
              {k === 'link' ? 'Paste link' : demo ? 'Search demo tracks' : 'Search YouTube'}
            </button>
          ))}
        </div>
      )}
      {mode === 'link' ? (
        <form
          className="flex flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            const v = input.trim();
            if (!v) return;
            if (!parseYouTubeInput(v)) {
              setErr('That does not look like a YouTube video link or id.');
              return;
            }
            add(v, toTop ? 'top' : 'bottom');
          }}
        >
          <label htmlFor={`add-${playlistId}`} className="sr-only">
            YouTube link or video id
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <LinkSimple
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
                aria-hidden
              />
              <Input
                id={`add-${playlistId}`}
                className="pl-9"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setErr(null);
                }}
                placeholder="Paste a YouTube link"
                aria-invalid={!!err}
                aria-describedby={err ? `add-err-${playlistId}` : undefined}
                maxLength={300}
                autoComplete="off"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              loading={m.addItem.isPending}
              aria-label="Add track"
            >
              <Plus size={16} weight="bold" aria-hidden />
              {!compact && 'Add'}
            </Button>
          </div>
          <label className="flex w-fit cursor-pointer items-center gap-2 text-xs text-fg-muted">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-accent"
              checked={toTop}
              onChange={(e) => setToTop(e.target.checked)}
            />
            Add to the top (plays sooner)
          </label>
          {demo && (
            <p className="text-[11px] leading-relaxed text-fg-subtle">
              Demo mode: only the built-in test tracks work (ids like{' '}
              <span className="font-mono">csdemo00001</span>–
              <span className="font-mono">csdemo00012</span>). Real YouTube links need a YouTube API
              key on the server.
            </p>
          )}
          {err && (
            <p id={`add-err-${playlistId}`} role="alert" className="text-xs text-[#f3a3a4]">
              {err}
            </p>
          )}
        </form>
      ) : (
        <SearchBox onAdd={(id) => add(id)} adding={m.addItem.isPending} />
      )}
    </div>
  );
}

function SearchBox({ onAdd, adding }: { onAdd: (videoId: string) => void; adding: boolean }) {
  const demo = useSession().data?.mediaMode === 'fake';
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Video[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState('');

  const run = async (pageToken?: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await searchMedia(q.trim(), pageToken);
      setResults((prev) => (pageToken ? [...prev, ...r.items] : r.items));
      setNext(r.nextPageToken);
      setSubmitted(q.trim());
    } catch (e) {
      setErr(
        e instanceof ApiError && e.code === 'QUOTA_EXHAUSTED'
          ? 'Keyword search is paused for today (the YouTube search budget is used up). Pasting a link still works.'
          : errorMessage(e),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <form
        role="search"
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim()) void run();
        }}
      >
        <label htmlFor="yt-search" className="sr-only">
          Search YouTube
        </label>
        <div className="relative flex-1">
          <MagnifyingGlass
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
            aria-hidden
          />
          <Input
            id="yt-search"
            className="pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Artist, track, set…"
            maxLength={100}
          />
        </div>
        <Button type="submit" loading={busy && results.length === 0}>
          Search
        </Button>
      </form>
      <p className="text-[11px] leading-relaxed text-fg-subtle">
        {demo ? (
          <>
            Demo mode: search covers only the ~20 built-in test tracks (try “signal” or “night”).
            Searching real YouTube needs a YouTube Data API key on the server.
          </>
        ) : (
          <>
            Search runs only when you submit. Results come from YouTube and are checked for
            embedding and length. Searches are limited per day, so pasting a link is often faster.
          </>
        )}
      </p>
      {err && (
        <p role="alert" className="text-xs text-warn">
          {err}
        </p>
      )}
      {submitted && results.length === 0 && !busy && !err && (
        <p className="text-sm text-fg-muted">
          {demo
            ? `No demo tracks match “${submitted}”. Demo mode can’t search real YouTube.`
            : `No embeddable results for “${submitted}”.`}
        </p>
      )}
      <ul className="flex flex-col gap-1">
        {results.map((v) => (
          <li
            key={v.videoId}
            className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-white/[0.03]"
          >
            <Thumb video={v} className="w-16" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{v.title}</p>
              <p className="truncate text-xs text-fg-muted">
                {v.channelTitle} · <span className="font-mono">{formatDuration(v.durationMs)}</span>
                {v.ineligibleReason && (
                  <span className="text-warn"> · {REASON_LABEL[v.ineligibleReason]}</span>
                )}
              </p>
            </div>
            <Button
              size="sm"
              disabled={!!v.ineligibleReason || adding}
              onClick={() => onAdd(v.videoId)}
              aria-label={`Add ${v.title}`}
            >
              <Plus size={14} weight="bold" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      {next && (
        <Button size="sm" variant="ghost" onClick={() => void run(next)} loading={busy}>
          More results
        </Button>
      )}
    </div>
  );
}
